import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

type AddressPreviewProps = {
  addressSubDwelling?: string | null;
  addressNameNumber?: string | null;
  street?: string | null;
  locality?: string | null;
  town?: string | null;
  county?: string | null;
  postcode?: string | null;
  emptyMessage?: string;
  action?: ReactNode;
  actionInFlow?: boolean;
  className?: string;
};

function clean(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function buildAddressLines({
  addressSubDwelling,
  addressNameNumber,
  street,
  locality,
  town,
  county,
  postcode,
}: Pick<
  AddressPreviewProps,
  | "addressSubDwelling"
  | "addressNameNumber"
  | "street"
  | "locality"
  | "town"
  | "county"
  | "postcode"
>): string[] {
  const line1 = [addressSubDwelling, addressNameNumber, street].map(clean).filter(Boolean).join(", ");
  const line2 = [locality, town, county].map(clean).filter(Boolean).join(", ");
  const line3 = clean(postcode);
  return [line1, line2, line3].filter(Boolean);
}

export function AddressPreview({
  addressSubDwelling,
  addressNameNumber,
  street,
  locality,
  town,
  county,
  postcode,
  emptyMessage = "No address on file",
  action,
  actionInFlow = false,
  className,
}: AddressPreviewProps) {
  const lines = buildAddressLines({
    addressSubDwelling,
    addressNameNumber,
    street,
    locality,
    town,
    county,
    postcode,
  });

  return (
    <div className={cn("group/copy relative", className)}>
      <div className={cn(
        "rounded-2xl border border-bridge-border/60 bg-white px-4 py-3 text-sm leading-relaxed text-bridge-fg shadow-sm",
        action && !actionInFlow && "pr-11",
      )}>
        {lines.length > 0 ? (
          lines.map((line, i) => <div key={`${line}-${i}`}>{line}</div>)
        ) : (
          <span className="italic text-bridge-muted">{emptyMessage}</span>
        )}
      </div>
      {action ? (
        <div className={actionInFlow ? "mt-2 flex justify-end" : "absolute right-3 top-2.5"}>
          {action}
        </div>
      ) : null}
    </div>
  );
}
